/**
 * Drill Log Detail Screen
 * View and edit details of a completed drill log
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Download, Clock, MapPin, User, Users, FileText, Edit, Save, X } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useReactToPrint } from 'react-to-print'

import logo from '@/assets/images/logo.png'
import { ActionsTimeline } from '@/components/DrillTimer/ActionsTimeline'
import { ResponderSelector } from '@/components/DrillTimer/ResponderSelector'
import { ResponderSignatures } from '@/components/DrillTimer/ResponderSignatures'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import { Skeleton } from '@/components/elements/skeleton'
import { Textarea } from '@/components/elements/textarea'
import { Layout } from '@/components/Layout'
import { drillTimerApi } from '@/services/drillTimerService'
import { getUsers } from '@/services/user.service'
import { toast } from '@/utils/helpers'

export function DrillLogDetailScreen() {
  const navigate = useNavigate()
  const { state: routeState } = useLocation()
  const { entityId, logId } = useParams()
  const queryClient = useQueryClient()
  const printRef = useRef(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editedData, setEditedData] = useState({})
  const [videoFile, setVideoFile] = useState(null)
  const [responderSignatures, setResponderSignatures] = useState({})

  const { data: log, isLoading } = useQuery({
    queryKey: ['drillLog', entityId, logId],
    queryFn: () => drillTimerApi.getLog(entityId, logId),
  })

  // Fetch available users for responder selection
  const { data: usersData } = useQuery({
    queryKey: ['users', entityId],
    queryFn: () =>
      getUsers({
        sorting: [],
        pagination: { pageIndex: 0, pageSize: 1000 },
        searchTerm: '',
        filters: { entity: entityId ? [entityId] : [] },
      }),
    staleTime: 5 * 60 * 1000,
    enabled: !!entityId,
  })

  const availableUsers = usersData?.results || []

  const updateMutation = useMutation({
    mutationFn: (data) => drillTimerApi.updateLog(entityId, logId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drillLog', entityId, logId] })
      queryClient.invalidateQueries({ queryKey: ['drillLogs', entityId] })
      setIsEditing(false)
      setEditedData({})
      toast('Drill log updated successfully', 'success')
    },
    onError: () => {
      toast('Failed to update drill log', 'error')
    },
  })

  const handleStartEdit = () => {
    setIsEditing(true)
    // Convert responders to the format expected by ResponderSelector
    const formattedResponders = (log.responders || []).map((responder) => ({
      user_id: responder.userId || responder.user_id || responder.id,
      user_name: responder.userName || responder.user_name || responder.name,
      roles: responder.roles || [],
      signature: responder.signature,
    }))

    // Initialize responder signatures
    const initialSignatures = {}
    formattedResponders.forEach((responder) => {
      if (responder.signature) {
        initialSignatures[responder.user_id] = responder.signature
      }
    })
    setResponderSignatures(initialSignatures)

    setEditedData({
      lessonsLearned: log.lessonsLearned || '',
      notes: log.notes || '',
      responders: formattedResponders,
    })
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedData({})
    setVideoFile(null)
    setResponderSignatures({})
  }

  const handleSave = async () => {
    const updatedData = { ...editedData }

    // Merge responder signatures into responders data
    if (updatedData.responders) {
      updatedData.responders = updatedData.responders.map((responder) => ({
        ...responder,
        signature: responderSignatures[responder.user_id] || responder.signature || null,
      }))
    }

    // If video file is selected, upload it first
    if (videoFile) {
      try {
        const response = await drillTimerApi.uploadVideo(entityId, videoFile, log.drillName)
        updatedData.video = response.url
      } catch (error) {
        toast('Failed to upload video. Saving other changes...', 'error')
      }
    }

    updateMutation.mutate(updatedData)
  }

  const handleExportPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: log?.drillName
      ? `Drill-${log.drillName}-${format(new Date(), 'yyyy-MM-dd')}`
      : 'Drill-Log',
  })

  // Auto-open the print dialog when navigated here via the logs list's PDF
  // button. Fires at most once; the state flag is cleared immediately so
  // back/refresh doesn't reopen the dialog.
  const autoPrintFired = useRef(false)
  useEffect(() => {
    if (!routeState?.autoPrint || !log || autoPrintFired.current) return
    autoPrintFired.current = true
    navigate('.', { replace: true, state: null })
    // Let the freshly loaded log paint before react-to-print snapshots the ref
    requestAnimationFrame(() => handleExportPDF())
  }, [routeState, log, navigate, handleExportPDF])

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`
    }
    return `${secs}s`
  }

  if (isLoading) {
    return (
      <Layout
        breadcrumbs={[
          { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
          'Drill Log',
        ]}
      >
        <Layout.Heading title="Drill Log" />
        <div className="flex flex-col gap-4">
          {/* Summary, timeline, and responders card placeholders */}
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    )
  }

  if (!log) {
    return (
      <Layout
        breadcrumbs={[
          { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
          'Not Found',
        ]}
      >
        <Layout.Heading title="Drill Log" />
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Drill log not found</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate(`/accounts/${entityId}/drill-timer/logs`)}
            >
              Back to Logs
            </Button>
          </CardContent>
        </Card>
      </Layout>
    )
  }

  // Data is already transformed to camelCase by axios interceptor
  const { drillName, startTime, endTime, lessonsLearned, notes, proctor, video } = log
  const location = log.locationOfDrill
  const records = log.records || []
  const responders = log.responders || []
  const proctorName = proctor?.userName
  const proctorRoles = proctor?.roles || []
  const proctorSignature = proctor?.signature
  const videoUrl = video

  // Calculate duration from start and end times if duration not provided
  let durationInSeconds = log.durationSeconds || log.duration
  if (!durationInSeconds && startTime && endTime) {
    const start = new Date(startTime)
    const end = new Date(endTime)
    durationInSeconds = Math.floor((end - start) / 1000)
  }

  return (
    <Layout
      breadcrumbs={[
        { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
        drillName,
      ]}
    >
      {/* Print-only styles */}
      <style>{`
        @media print {
          /* Hide navigation, buttons, and edit controls */
          nav, button, .no-print {
            display: none !important;
          }

          /* Hide videos and signatures in print */
          video, .signature-image {
            display: none !important;
          }

          /* Ensure images print well */
          img:not(.signature-image) {
            max-width: 100% !important;
            page-break-inside: avoid;
          }

          /* Ensure colors print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Page breaks */
          h2 {
            page-break-after: avoid;
          }
          .page-break {
            page-break-before: always;
          }

          /* Show print header */
          .print-header {
            display: block !important;
          }

          /* Hide screen heading */
          .screen-heading {
            display: none !important;
          }

          /* Add padding to print content */
          .print-content {
            padding: 2rem !important;
          }

          /* Remove padding from header for full-width color */
          .print-header {
            margin: -2rem -2rem 2rem -2rem !important;
          }
        }

        /* Hide print header on screen */
        @media screen {
          .print-header {
            display: none !important;
          }
        }
      `}</style>

      <div className="screen-heading no-print">
        <Layout.Heading title={drillName}>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleExportPDF}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export PDF
                </Button>
                <Button onClick={handleStartEdit} className="flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </>
            )}
          </div>
        </Layout.Heading>
      </div>

      <div ref={printRef} className="print-content">
        {/* Print-only fancy header */}
        <div className="print-header bg-primary p-8 text-white">
          <div className="mb-2 flex items-center gap-3">
            <img src={logo} alt="RescueX" className="h-8" />
          </div>
          <h1 className="mb-2 text-3xl">{drillName}</h1>
          <p className="text-gray-300">Drill Log Report</p>
          <p className="mt-1 text-sm text-gray-400">{location}</p>
        </div>

        <div className="space-y-6">
          {/* Overview Card */}
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div className="flex items-start gap-3">
                  <MapPin className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <div>
                    <div className="text-muted-foreground text-sm">Location</div>
                    <div className="font-medium">{location || '—'}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <div>
                    <div className="text-muted-foreground text-sm">Date & Time</div>
                    <div className="font-medium">
                      {startTime ? format(new Date(startTime), 'MMM d, yyyy h:mm a') : '—'}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <div>
                    <div className="text-muted-foreground text-sm">Duration</div>
                    <div className="font-medium">
                      {durationInSeconds ? formatDuration(durationInSeconds) : '—'}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FileText className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <div>
                    <div className="text-muted-foreground text-sm">Total Actions</div>
                    <div className="font-medium">{records.length}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Proctor Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Proctor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="text-lg font-medium">{proctorName || '—'}</div>
                  {proctorRoles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {proctorRoles.map((role) => (
                        <Badge key={role} variant="secondary">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-muted-foreground mb-2 text-sm">Signature</div>
                  {proctorSignature ? (
                    <img
                      src={proctorSignature}
                      alt="Proctor signature"
                      className="h-24 max-w-xs rounded border bg-white object-contain p-2"
                    />
                  ) : (
                    <div className="text-muted-foreground text-sm italic">
                      No signature provided
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Responders Card */}
          {isEditing ? (
            <>
              <ResponderSelector
                availableUsers={availableUsers}
                availableRoles={log.roles || []}
                value={editedData.responders || []}
                onRespondersChange={(updatedResponders) =>
                  setEditedData({ ...editedData, responders: updatedResponders })
                }
              />
              {editedData.responders && editedData.responders.length > 0 && (
                <ResponderSignatures
                  responders={editedData.responders}
                  onSignaturesChange={setResponderSignatures}
                />
              )}
            </>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Responders ({responders.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {responders.length === 0 ? (
                  <div className="text-muted-foreground py-8 text-center text-sm italic">
                    No responders recorded
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {responders.map((responder) => {
                      const responderName = responder.userName || responder.name
                      const responderRoles = responder.roles || []
                      const responderSignature = responder.signature
                      const responderId = responder.id || responder.userId

                      return (
                        <div key={responderId} className="space-y-3 rounded-lg border p-4">
                          <div>
                            <div className="text-lg font-medium">
                              {responderName || 'Unknown Responder'}
                            </div>
                            {responderRoles.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {responderRoles.map((role) => (
                                  <Badge key={role} variant="outline" className="text-xs">
                                    {role}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-muted-foreground mb-1 text-xs">Signature</div>
                            {responderSignature ? (
                              <img
                                src={responderSignature}
                                alt={`${responderName} signature`}
                                className="h-20 w-full rounded border bg-white object-contain p-2"
                              />
                            ) : (
                              <div className="text-muted-foreground text-xs italic">
                                No signature provided
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions Timeline Card */}
          <ActionsTimeline records={records} startTime={startTime} />

          {/* Lessons Learned Card */}
          <Card>
            <CardHeader>
              <CardTitle>Lessons Learned</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                if (isEditing) {
                  return (
                    <Textarea
                      value={editedData.lessonsLearned || ''}
                      onChange={(e) =>
                        setEditedData({ ...editedData, lessonsLearned: e.target.value })
                      }
                      placeholder="Enter lessons learned from this drill..."
                      rows={6}
                      className="w-full"
                    />
                  )
                }
                if (lessonsLearned) {
                  return <p className="whitespace-pre-wrap">{lessonsLearned}</p>
                }
                return (
                  <p className="text-muted-foreground text-sm italic">
                    No lessons learned recorded
                  </p>
                )
              })()}
            </CardContent>
          </Card>

          {/* Additional Notes Card */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                if (isEditing) {
                  return (
                    <Textarea
                      value={editedData.notes || ''}
                      onChange={(e) => setEditedData({ ...editedData, notes: e.target.value })}
                      placeholder="Enter additional notes..."
                      rows={6}
                      className="w-full"
                    />
                  )
                }
                if (notes) {
                  return <p className="whitespace-pre-wrap">{notes}</p>
                }
                return (
                  <p className="text-muted-foreground text-sm italic">
                    No additional notes recorded
                  </p>
                )
              })()}
            </CardContent>
          </Card>

          {/* Extrication Video Card */}
          <Card>
            <CardHeader>
              <CardTitle>Extrication Video</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                if (isEditing) {
                  return (
                    <div className="space-y-4">
                      {videoUrl && (
                        <div>
                          <div className="text-muted-foreground mb-2 text-sm">Current Video:</div>
                          <video controls className="w-full max-w-2xl rounded-lg" src={videoUrl}>
                            <track kind="captions" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )}
                      <div>
                        <label
                          htmlFor="video-upload"
                          className="text-muted-foreground mb-2 block text-sm"
                        >
                          {videoUrl ? 'Replace Video:' : 'Upload Video:'}
                        </label>
                        <Input
                          id="video-upload"
                          type="file"
                          accept="video/*"
                          onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                          className="w-full max-w-md"
                        />
                        {videoFile && (
                          <p className="text-muted-foreground mt-2 text-sm">
                            Selected: {videoFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                }
                if (videoUrl) {
                  return (
                    <video controls className="w-full max-w-2xl rounded-lg" src={videoUrl}>
                      <track kind="captions" />
                      Your browser does not support the video tag.
                    </video>
                  )
                }
                return <p className="text-muted-foreground text-sm italic">No video uploaded</p>
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  )
}
